import React, { useState, useEffect } from 'react';
import { Trash2, Mail, Calendar, Briefcase, Download, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { jobApplicationsAPI, getUploadUrl } from '../../services/api';

const JobApplicationsTab = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadCv = async (id) => {
    setDownloadingId(id);
    try {
      const response = await jobApplicationsAPI.getCvUrl(id);
      const url = response.data?.url;
      if (!url) throw new Error('no-url');
      window.open(getUploadUrl(url), '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error('Impossibile generare il link di download del CV');
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      const response = await jobApplicationsAPI.getAll();
      setApplications(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle candidature');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa candidatura?')) return;
    try {
      await jobApplicationsAPI.delete(id);
      toast.success('Candidatura eliminata!');
      loadApplications();
    } catch (error) {
      toast.error('Errore nell\'eliminare la candidatura');
    }
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <CardTitle className="text-2xl">Candidature</CardTitle>
            <CardDescription>Gestisci le candidature ricevute</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Posizione</TableHead>
                <TableHead>CV</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">Caricamento...</TableCell></TableRow>
              ) : applications.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nessuna candidatura</TableCell></TableRow>
              ) : (
                applications.map((app) => (
                  <TableRow key={app.id} data-testid={`job-app-row-${app.id}`}>
                    <TableCell className="font-medium">{app.first_name} {app.last_name}</TableCell>
                    <TableCell>
                      <a href={`mailto:${app.email}`} className="text-primary hover:underline flex items-center">
                        <Mail className="w-4 h-4 mr-1" />
                        {app.email}
                      </a>
                    </TableCell>
                    <TableCell><Badge variant="secondary">{app.position}</Badge></TableCell>
                    <TableCell>
                      {app.cv_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadCv(app.id)}
                          disabled={downloadingId === app.id}
                          data-testid={`download-cv-${app.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {downloadingId === app.id ? '...' : 'CV'}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-muted-foreground">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(app.created_at).toLocaleDateString('it-IT')}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSelectedApp(app)}
                          data-testid={`view-job-${app.id}`}
                        >
                          Dettagli
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDelete(app.id)} 
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-job-${app.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Detail Dialog */}
        <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Dettaglio Candidatura</DialogTitle>
              <DialogDescription>Informazioni complete sul candidato</DialogDescription>
            </DialogHeader>
            {selectedApp && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Nome</p>
                    <p className="font-medium">{selectedApp.first_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Cognome</p>
                    <p className="font-medium">{selectedApp.last_name}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedApp.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Telefono</p>
                    <p className="font-medium">{selectedApp.phone}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Posizione</p>
                  <Badge className="mt-1">{selectedApp.position}</Badge>
                </div>
                {selectedApp.message && (
                  <div>
                    <p className="text-sm text-muted-foreground">Messaggio</p>
                    <div className="mt-2 p-4 rounded-lg bg-muted">
                      <p className="whitespace-pre-wrap">{selectedApp.message}</p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Curriculum Vitae</p>
                  {selectedApp.cv_url ? (
                    <Button
                      className="mt-2"
                      onClick={() => handleDownloadCv(selectedApp.id)}
                      disabled={downloadingId === selectedApp.id}
                      data-testid={`download-cv-detail-${selectedApp.id}`}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {downloadingId === selectedApp.id ? 'Generazione link...' : 'Scarica CV'}
                    </Button>
                  ) : (
                    <p className="text-muted-foreground">Nessun CV allegato</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data candidatura</p>
                  <p className="font-medium">{new Date(selectedApp.created_at).toLocaleString('it-IT')}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default JobApplicationsTab;
